import { normaliseQuotePunctuation } from '../utils/scope-descriptions';
import { classifySharedSiteLogisticsInclusion } from '../utils/scope-logistics-dedupe';
import { HOUSE_STANDARD_NUMBER, HOUSE_TITLE_BUDGET } from '../config/scope-writing-contract.js';

// The house title budget lives with the contract (one copy of the numbers):
// at most 60 characters and 10 words, aiming at 50. Measured on RJ Bird's 220
// sent scope titles (median 34 characters, p90 49, max 57, at most 10 words,
// none with a standard number or a closing full stop).
export { HOUSE_TITLE_BUDGET } from '../config/scope-writing-contract.js';

const INTERNAL_STRATEGY_JARGON = [
    /\bevidence[ -](?:backed|based|grounded|led)\b/i,
    /\bcause[ -](?:based|first|led)\b/i,
    /\bcausal\s+(?:rectification|remediation|repairs?(?:\s+(?:plan|strategy))?)\b/i,
    /\b(?:chosen|selected|recommended)(?:\s+\w+){0,2}\s+approach\b/i,
    /\b(?:diagnosis|diagnostic)[ -](?:anchored|driven|led)\b/i,
];

const TECHNICAL_REGISTER_TITLE_DETAIL = /(?:\bin accordance with\b[\s\S]*\b(?:consult(?:ant|ing)?|engineer(?:ing|ed|s)?|design|drawings?|specifications?)\b|\b(?:consult(?:ant|ing)?|engineer(?:ing|ed|s)?)\b[\s\S]{0,80}\b(?:designs?|drawings?|specifications?|details?)\b|\b(?:designs?|drawings?|specifications?)\b[\s\S]{0,80}\b(?:consult(?:ant|ing)?|engineer(?:ing|ed|s)?)\b|\bregulated\s+design\s+drawings?\b|\bdrawings?\s+[A-Z](?:\.|-)?\d)/i;

export const GENERATED_TITLE_REVIEW_MARKER = 'Scope title requires builder review';

const GENERIC_SCOPE_TITLE = /^(?:(?:builder\s+review\s+required\s*[-:]?\s*)?)?(?:scope\s+title\s+requires\s+builder\s+review|(?:the\s+)?(?:(?:client[ -])?authori[sz]ed\s+)?(?:(?:physical|general|final|client)\s+)?(?:scope|works?|repair|repairs|rectification|remediation)(?:\s+(?:of\s+works|works?|package|plan|strategy|approach|scope))?)$/i;
const GENERIC_SCOPE_TITLE_AFTER_LOCATION = /^.{1,100}\s+-\s+(?:(?:client[ -])?authori[sz]ed\s+)?(?:(?:physical|general|final|client)\s+)?(?:scope|works?|repair|repairs|rectification|remediation)(?:\s+(?:of\s+works|works?|package|plan|strategy|approach|scope))?$/i;
const GENERIC_OBJECTIVE_ACTION = /^(?:the\s+)?(?:(?:client[ -])?authori[sz]ed\s+)?(?:(?:physical|general|final|client)\s+)?(?:scope|works?|repair|repairs|rectification|remediation)(?:\s+(?:of\s+works|works?|package|plan|strategy|approach|scope))?(?:\s+(?:described|listed|set\s+out)\s+below)?(?:\s+(?:at|for|in|on|to|within)\b[\s\S]*)?$/i;

const USER_EDITORS = new Set([
    'user',
    'user-edit',
    'manual',
    'operator',
    'assistant',
    'assistant-edit',
]);

function clean(value) {
    return normaliseQuotePunctuation(String(value || ''))
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function textValues(value) {
    if (typeof value === 'string' || typeof value === 'number') {
        const text = clean(value);
        return text ? [text] : [];
    }
    if (!value || typeof value !== 'object') return [];
    if (Array.isArray(value)) return value.flatMap(textValues);
    return [
        value.title,
        value.name,
        value.label,
        value.location,
        value.workArea,
        value.work_area,
        value.area,
        value.zone,
        value.description,
        value.descriptionBrief,
        value.description_brief,
        value.clientDescription,
        value.notes_to_client,
        value.action,
        value.method,
        value.why_this_method,
        value.inclusions,
        value.sequence_of_works,
        value.remediation_sequence,
        value.client_scope_override,
    ].flatMap(textValues);
}

function evidenceText(item, sourceItems = []) {
    return textValues([item, ...(Array.isArray(sourceItems) ? sourceItems : [])]).join(' ');
}

function physicalActionEvidenceText(item, sourceItems = []) {
    const physicalFields = (value) => {
        if (!value || typeof value !== 'object') return [];
        return [
            value.description,
            value.descriptionBrief,
            value.description_brief,
            value.clientDescription,
            value.notes_to_client,
            value.action,
            value.method,
            value.inclusions,
            value.sequence_of_works,
            value.remediation_sequence,
            value.client_scope_override?.description,
            value.client_scope_override?.inclusions,
        ].flatMap(textValues);
    };
    return [item, ...(Array.isArray(sourceItems) ? sourceItems : [])]
        .flatMap(physicalFields)
        .join(' ');
}

function titleCase(value) {
    const minor = new Set(['a', 'an', 'and', 'at', 'for', 'in', 'of', 'on', 'the', 'to', 'with']);
    return clean(value)
        .toLowerCase()
        .split(' ')
        .map((word, index) => (index > 0 && minor.has(word)
            ? word
            : word.charAt(0).toUpperCase() + word.slice(1)))
        .join(' ');
}

function hasInternalStrategyJargon(value) {
    const text = clean(value);
    return INTERNAL_STRATEGY_JARGON.some(pattern => pattern.test(text));
}

function hasTechnicalRegisterTitleDetail(value, { allowReferencedDesign = false } = {}) {
    let text = clean(value);
    if (allowReferencedDesign) {
        text = text.replace(/\bin accordance with the referenced engineering design\b/gi, '');
    }
    return TECHNICAL_REGISTER_TITLE_DETAIL.test(text);
}

/**
 * True only for internal strategy/process labels, technical-register detail
 * that belongs below the title, or content-free headings.
 * Specific construction titles such as "Spalling Concrete Remediation" are
 * intentionally accepted: remediation is valid trade language when the title
 * also names the physical element being repaired.
 */
export function isInternalStrategyScopeTitle(value) {
    const title = clean(value);
    if (!title) return true;
    return hasInternalStrategyJargon(title)
        || hasTechnicalRegisterTitleDetail(title)
        || GENERIC_SCOPE_TITLE.test(title)
        || GENERIC_SCOPE_TITLE_AFTER_LOCATION.test(title);
}

// A unit or lot id holds a digit ("Unit 5", "Lot 12A") or is one capital
// letter ("Unit A", "Lot B" on a dual occupancy). The letter is matched
// case-sensitively, so "the unit entry" and "the unit a few doors down" name
// no unit.
const UNIT_OR_LOT_SOURCE = String.raw`\b([Uu]nit|UNIT|[Ll]ot|LOT|[Aa]partment|APARTMENT)\s+([A-Za-z0-9-]*\d[A-Za-z0-9-]*|[A-Z](?![A-Za-z0-9]))\b`;
const UNIT_OR_LOT_TOKENS = new RegExp(UNIT_OR_LOT_SOURCE, 'g');

// "Unit 5", "Lot 12A": the unit or lot tokens a text names, lower-cased.
function unitOrLotTokens(value) {
    return [...clean(value).matchAll(UNIT_OR_LOT_TOKENS)]
        .map(match => `${match[1]} ${match[2]}`.toLowerCase());
}

// HOUSE_TITLE_RULES allow the work alone for whole-building work or "one
// element whose name says where it is" ("Gutter Guard Installation"). These
// are building and common-property elements: a strata RFQ often names the
// unit that reported a leak from them ("leaking into Unit 5 from the roof").
// A unit's own elements (balcony, shower, window, door, tiles) are not here.
const SELF_LOCATING_ELEMENT = /\b(?:roof(?:s|ing)?|gutters?|downpipes?|rainwater\s+heads?|fascias?|eaves?|parapets?|fa[cç]ades?|driveways?|car\s?parks?|basement|footpaths?|fenc(?:e|es|ing)|retaining\s+walls?|boundary\s+walls?|letterbox(?:es)?|lifts?|lobby|foyer|stairwells?|common\s+(?:property|areas?)|building|bin\s+(?:rooms?|areas?|enclosures?))\b/i;

/**
 * The house title check (every business). Each issue is { code, severity,
 * detail }:
 *   too-long         fail  over HOUSE_TITLE_BUDGET.maxChars (60) characters
 *   standard-number  fail  an AS, AS/NZS, NZS, NCC or BCA number
 *   sentence         fail  ends with a full stop, runs past
 *                          HOUSE_TITLE_BUDGET.maxWords (10) words, or holds
 *                          a ", and" clause
 *   no-location      warn  the evidence names a unit or lot, and the title
 *                          names none of them, has no "<Location> -
 *                          <Work>" dash and names no building element whose
 *                          name says where it is (SELF_LOCATING_ELEMENT).
 *                          Sent quotes title a unit's work without the dash
 *                          ("Unit 2 Laundry Wall Water Ingress"), so naming
 *                          the unit is enough. A warning never triggers a
 *                          repair; the Stage 3 fold uses it to choose
 *                          another source field.
 * Words are tokens holding a letter or digit, so "-" and "&" are not words.
 */
export function scopeTitleContractIssues(title, { evidence = '' } = {}) {
    const text = clean(title);
    const issues = [];
    if (!text) return issues;
    if (text.length > HOUSE_TITLE_BUDGET.maxChars) {
        issues.push({ code: 'too-long', severity: 'fail', detail: `${text.length} characters (at most ${HOUSE_TITLE_BUDGET.maxChars}, aim for ${HOUSE_TITLE_BUDGET.targetChars})` });
    }
    if (HOUSE_STANDARD_NUMBER.test(text)) {
        issues.push({ code: 'standard-number', severity: 'fail', detail: 'a standard number belongs in an inclusion line' });
    }
    const words = text.split(/\s+/).filter(word => /[A-Za-z0-9]/.test(word)).length;
    const sentenceSigns = [
        /\.$/.test(text) ? 'a closing full stop' : '',
        words > HOUSE_TITLE_BUDGET.maxWords ? `${words} words (at most ${HOUSE_TITLE_BUDGET.maxWords})` : '',
        /,\s+and\b/i.test(text) ? 'a ", and" clause' : '',
    ].filter(Boolean);
    if (sentenceSigns.length > 0) {
        issues.push({ code: 'sentence', severity: 'fail', detail: sentenceSigns.join(', ') });
    }
    const evidenceUnits = unitOrLotTokens(evidence);
    if (evidenceUnits.length > 0 && !/\s-\s/.test(text) && !SELF_LOCATING_ELEMENT.test(text)) {
        const titleUnits = new Set(unitOrLotTokens(text));
        if (!evidenceUnits.some(unit => titleUnits.has(unit))) {
            issues.push({ code: 'no-location', severity: 'warn', detail: 'the job names a unit or lot; titles read "<Location> - <Work>"' });
        }
    }
    return issues;
}

/** True when the title has no failing house title issue (warnings allowed). */
export function scopeTitlePassesContract(title) {
    return !scopeTitleContractIssues(title).some(issue => issue.severity === 'fail');
}

// A generated title that is internal strategy wording, cites a standard or is
// shaped like a sentence is replaced by another source field when one passes.
// A builder's title never is. Only internal wording goes on to the evidence
// derivation in resolveGeneratedClientScopeTitle: a title that names the work
// in the wrong shape is kept (the readiness warning scope-title-contract and
// the Stage 4 title hint flag it, scope-copy-contract.js),
// because the derivation picked the wrong work for such titles (2026-10-12:
// "Unit 3 Bathroom - Shower Recess Waterproofing to AS 3740" became
// "Unit 3 - The Shower Recess Floor Tiles Removal").
// A label that is only over-long is kept too: Peter's rule wraps it on the
// quote ("Car Park - Galvanised Chain Wire Storage Cage (Price Per Bay)" became
// "Floor Protection Installation", and three 73 to 79 character
// "<Location> - <Work>" titles became the review marker).
const TITLE_REPAIR_CODES = new Set(['standard-number', 'sentence']);

export function scopeTitleNeedsRepair(title) {
    return isInternalStrategyScopeTitle(title)
        || scopeTitleContractIssues(title).some(issue => TITLE_REPAIR_CODES.has(issue.code));
}

/**
 * Positive authorship proof. QuoteMaker has old AI/WMS rows carrying a stale
 * `manuallyEdited` flag, so an explicit generated editor can rebut that flag.
 * Ambiguous legacy manual rows remain protected: without a generated stamp we
 * cannot prove the builder did not write them.
 */
export function isExplicitUserAuthoredScopeItem(item = {}) {
    const editor = clean(item?.editedBy || item?.scopeEditedBy || item?.edited_by || item?.scope_edited_by)
        .toLowerCase();
    const explicitHuman = item?._scopeAuthoritySource === 'operator-authored'
        || item?.titleEditedByUser === true
        || item?.title_edited_by_user === true
        || USER_EDITORS.has(editor)
        || Boolean(
            item?.humanEditedAt
            || item?.human_edited_at
            || item?.scopeEditedAt
            || item?.scope_edited_at
            || item?.titleEditedAt
            || item?.title_edited_at,
        );
    if (explicitHuman) return true;
    if (item?.manuallyEdited !== true && item?.manually_edited !== true) return false;
    return !['ai-initial', 'wms-writeback', 'system', 'quote-final'].includes(editor);
}

// Areas and building elements a title names its place with, with or without
// a dash ("Laundry Floor Waste Replacement", "Balcony Waterproofing", "Entry
// Foyer Drainage"). Surfaces and fittings (floor, wall, tiles, shower, window)
// name the work, not the place. A lounge, lift, corridor, walkway, elevation
// or level is a place too ("Lounge Window Resealing." became "Window
// resealing" before they were listed).
const PLACE_WORDS = /\b(?:balcon(?:y|ies)|terraces?|courtyards?|patios?|decks?|verandahs?|porch(?:es)?|pergolas?|ensuites?|bathrooms?|laundr(?:y|ies)|kitchens?|bedrooms?|lounge(?:\s+rooms?)?|living\s+(?:rooms?|areas?)|dining\s+(?:rooms?|areas?)|hallways?|corridors?|walkways?|lifts?|elevations?|level\s+\d+[a-z]?|toilets?|garages?|carports?|stair(?:wells?|cases?|s)?|foyers?|lobb(?:y|ies)|entry|entrance|roof(?:s|ing)?|gutters?|downpipes?|rainwater\s+heads?|fascias?|eaves?|soffits?|parapets?|fa[cç]ades?|driveways?|car\s?parks?|basements?|footpaths?|fenc(?:e|es|ing)|retaining\s+walls?|boundary\s+walls?|letterbox(?:es)?|common\s+(?:property|areas?)|building|bin\s+(?:rooms?|areas?|enclosures?)|plant\s+rooms?|gardens?|planters?)\b/gi;
const PLACE_STOP_WORDS = new Set(['a', 'an', 'and', 'at', 'for', 'in', 'of', 'on', 'the', 'to', 'with']);

// One spelling per place word: lower case, singular ("Balconies" and
// "balcony", "Roofing" and "Roof" are one place).
function placeKey(value) {
    return clean(value).toLowerCase()
        .replace(/ç/g, 'c')
        .replace(/^roof(?:s|ing)$/, 'roof')
        .replace(/^fenc(?:es|ing)$/, 'fence')
        .replace(/ies$/, 'y')
        .replace(/(ch|x)es$/, '$1')
        .replace(/([a-z]{3})s$/, '$1');
}

function titleWordKeys(value) {
    return clean(value).toLowerCase().split(/[^a-z0-9ç]+/)
        .filter(word => word && !PLACE_STOP_WORDS.has(word))
        .map(placeKey);
}

function placeWordKeys(value) {
    return [...clean(value).matchAll(PLACE_WORDS)].map(match => placeKey(match[0]));
}

// A replacement title never carries less place information than the title it
// replaces. Every unit or lot that title names, every word of its location
// before a "<Location> - <Work>" dash, and every area or building element
// word it names (PLACE_WORDS) is in the replacement too. Otherwise the
// generated title stays and the readiness title warning asks the builder.
// "Unit 7 - Roof Leak Repairs" never becomes "Roof Sheet Replacement" (which
// unit's roof is priced matters), "Unit 5 Balcony - Membrane Renewal." never
// becomes "Unit 5 - Membrane Application", and "Ensuite Shower Recess
// Waterproofing to AS 3740" never becomes "Tile removal" (2026-10-12, Stage 4
// Refresh from Work Method).
function keepsPlaceOf(candidate, current) {
    const was = clean(current);
    const next = clean(candidate);
    const nextUnits = new Set(unitOrLotTokens(next));
    if (!unitOrLotTokens(was).every(unit => nextUnits.has(unit))) return false;
    const [location, ...work] = was.split(/\s-\s/);
    if (work.length > 0) {
        const nextWords = new Set(titleWordKeys(next));
        if (!titleWordKeys(location).every(word => nextWords.has(word))) return false;
    }
    const nextPlaces = new Set(placeWordKeys(next));
    return placeWordKeys(was).every(place => nextPlaces.has(place));
}

// The row's own words that say where the work is: its title and Scope
// Description. The no-location rule reads them as evidence.
function itemLocationEvidence(item = {}) {
    return [
        item?.title,
        item?.description,
        item?.descriptionBrief,
        item?.description_brief,
        item?.clientDescription,
    ].map(clean).filter(Boolean).join(' ');
}

function namesLocationFor(title, evidence) {
    return !scopeTitleContractIssues(title, { evidence }).some(issue => issue.code === 'no-location');
}

// An "<AREA> - <WORK>" subheading names one area of the item. A row whose
// subheadings name two or more areas ("UNIT 5 BALCONY - ...", "UNIT 5
// WINDOWS - ...") is several pieces of work under one price, and a title
// derived from one work type names only one of them.
function subheadingAreas(item = {}) {
    const lines = Array.isArray(item?.inclusions) ? item.inclusions : [];
    return new Set(lines
        .filter(line => line && typeof line === 'object' && line.type === 'heading')
        .map(line => clean(line.text))
        .filter(text => /\s-\s/.test(text))
        .map(text => text.split(/\s-\s/)[0].trim().toLowerCase())
        .filter(Boolean));
}

// isSafe decides which source titles count: titles that pass the house check,
// or (second choice) any physical title, even one in the wrong shape.
function explicitSafeTitle(sourceItems = [], isSafe = title => !scopeTitleNeedsRepair(title)) {
    const sources = (Array.isArray(sourceItems) ? sourceItems : [])
        .filter(source => source && typeof source === 'object');
    const safe = value => {
        const title = clean(value);
        return title && isSafe(title) ? title : '';
    };
    const explicitOverrides = [...new Set(sources
        .map(source => safe(source?.client_scope_override?.title))
        .filter(Boolean))];
    if (explicitOverrides.length === 1) return explicitOverrides[0];

    // A sequence-step title (for example "Install floor protection") is not
    // authority for the whole client scope item. Reuse a source title only
    // when this is genuinely a one-source row, or every folded source agrees
    // on the same physical title. Otherwise derive from the aggregate work.
    const directTitles = sources.map(source => safe(source?.title));
    const nonEmptyDirectTitles = directTitles.filter(Boolean);
    const uniqueDirectTitles = [...new Set(nonEmptyDirectTitles)];
    if (sources.length === 1 && uniqueDirectTitles.length === 1) return uniqueDirectTitles[0];
    if (sources.length > 1
        && nonEmptyDirectTitles.length === sources.length
        && uniqueDirectTitles.length === 1) return uniqueDirectTitles[0];
    return '';
}

function physicalLocation(text) {
    // A unit or lot has a number or a capital letter: "the unit entry" and
    // "the unit below" name no unit (UNIT_OR_LOT_SOURCE). The id is cased on
    // its own, so "Unit A" never prints as "Unit a".
    const unitOrLot = text.match(new RegExp(UNIT_OR_LOT_SOURCE));
    const unitLocation = unitOrLot
        ? `${titleCase(unitOrLot[1])} ${titleCase(unitOrLot[2])}`
        : '';

    const named = [
        [/\bexterior\s+corner\s+above\s+(?:the\s+)?main\s+bedroom\b/i, 'Main Bedroom Exterior Corner'],
        [/\b(?:rear|western)\s+terrace\b/i, 'Rear Terrace'],
        [/\bexterior\s+terrace\b/i, 'Exterior Terrace'],
        [/\bterrace\b/i, 'Terrace'],
        [/\bbasement\s+car\s*park\s+ramp\b/i, 'Basement Carpark Ramp'],
        [/\bbasement\b[\s\S]{0,180}\bdriveway\b|\bdriveway\b[\s\S]{0,180}\bbasement\b/i, 'Basement Driveway'],
        [/\bupper balcony\b/i, 'Upper Balcony'],
        [/\blower balcony\b/i, 'Lower Balcony'],
        [/\brear balcony\b/i, 'Rear Balcony'],
        [/\bfront balcony\b/i, 'Front Balcony'],
        [/\bbalcony\b/i, 'Balcony'],
        [/\bwestern courtyard\b/i, 'Western Courtyard'],
        [/\beastern courtyard\b/i, 'Eastern Courtyard'],
        [/\brear courtyard\b/i, 'Rear Courtyard'],
        [/\bfront courtyard\b/i, 'Front Courtyard'],
        [/\bcourtyard\b/i, 'Courtyard'],
        [/\bgarage soffit\b/i, 'Garage Soffit'],
        [/\bbasement\b/i, 'Basement'],
        [/\bdriveway\b/i, 'Driveway'],
        [/\bfull roof\b/i, 'Full Roof'],
        [/\broof\b/i, 'Roof'],
        [/\bcommon property foyer\b/i, 'Common Property Foyer'],
        [/\bfoyer\b/i, 'Foyer'],
    ];
    const physicalArea = named.find(([pattern]) => pattern.test(text))?.[1] || '';
    if (unitLocation && physicalArea) return `${unitLocation} ${physicalArea}`;
    return unitLocation || physicalArea;
}

function physicalWorkType(text) {
    const has = (pattern) => pattern.test(text);
    // Split before cleaning, so a line break between fields ends a clause.
    const clauses = String(text || '').split(/[.;\n]+/).map(clean).filter(Boolean);
    const actionBefore = (actions, elements) => {
        // Only accept an action whose grammatical object follows in the same
        // clause, with no intervening location preposition. This rejects
        // coincidences such as "replace damaged render below the window".
        const pattern = new RegExp(
            `\\b(?:${actions})\\b\\s+(?:(?!\\b(?:above|across|along|around|at|below|beside|by|for|from|in|near|on|to|under|within)\\b)[A-Za-z0-9/&()'-]+\\s+){0,5}(?:${elements})\\b`,
            'i',
        );
        return clauses.some(clause => pattern.test(clause));
    };
    const nounAction = (elements, actions) => new RegExp(
        `\\b(?:${elements})\\s+(?:${actions})\\b`,
        'i',
    ).test(text);
    const eaveRepair = actionBefore('repair|replace|renew|reinstate|make good', 'eaves?|soffit')
        || nounAction('eaves?|soffit', 'repair|repairs|replacement|renewal|reinstatement');
    const gutterClearing = actionBefore('clear|clean|flush|jet', 'gutters?|gutter run')
        || nounAction('gutters?|gutter run', 'clearing|cleaning|flushing');
    if (has(/\bterrace\b/i) && has(/\bwaterproof\w*|\bmembrane\b/i) && has(/\bconcrete\s+substrate\b/i)) {
        return 'Waterproofing and Concrete Substrate Rectification';
    }
    if (has(/\btrench\s+drain\b/i) && has(/\brebate\b/i)) {
        return 'Trench Drain Rebate Repair';
    }
    if (eaveRepair && gutterClearing) return 'Eave Repair and Gutter Clearing';
    // Delaminated topping or coating on a balcony or terrace that is being
    // waterproofed is preparation for the membrane, not spalling: the
    // 2026-10-11 Unit 5 job derived "Spalling Concrete Repair" from "break
    // out drummy and delaminated topping". Spalling itself still wins.
    const membraneDeck = has(/\b(?:balcony|terrace)\b/i) && has(/\bwaterproof\w*|\bmembrane\b/i);
    if (has(/\bconcrete\b/i) && (has(/\bspall\w*/i) || (!membraneDeck && has(/\bdelaminat\w*/i)))) {
        return 'Spalling Concrete Repair';
    }
    if (membraneDeck) return 'Waterproofing';
    // Read in one clause: a reason often names the shower where a gutter or
    // window leak shows ("leaks into the Unit 4 shower below"), and a
    // membrane or "waterproof silicone" elsewhere in the job is not shower
    // work.
    if (clauses.some(clause => /\bshower\b/i.test(clause) && /\bwaterproof\w*|\bmembrane\b/i.test(clause))) {
        return 'Shower Recess Waterproofing';
    }
    if (has(/\bdriveway\b/i) && has(/\bconcrete\b/i)
        && (actionBefore('repair|replace|reconstruct|reinstate|break out', 'concrete|driveway|slab')
            || nounAction('concrete|driveway|slab', 'repair|repairs|replacement|reconstruction|reinstatement'))) {
        return 'Concrete Driveway Repair';
    }
    if (has(/\brender\b/i) && has(/\bcrack\w*/i)) return 'Render Crack Repair';
    if (has(/\bplaster\w*\b/i) && has(/\bcrack\w*/i)) return 'Plaster Crack Repair';
    if (has(/\bbrick(?:work)?\b/i)) {
        const rebuilding = actionBefore('rebuild|replace|renew', 'brick(?:work)?|bricks?')
            || nounAction('brick(?:work)?|bricks?', 'rebuilding|replacement|renewal');
        if (rebuilding) return 'Brickwork Rebuilding';
        const repair = actionBefore('make good|repair|repoint|reinstate', 'brick(?:work)?|bricks?')
            || nounAction('brick(?:work)?|bricks?', 'repair|repairs|repointing|reinstatement');
        if (repair) return 'Brickwork Repair';
    }
    if (has(/\bwindow\b/i) && has(/\bsealant|\breseal|\bseal\b/i)) return 'Window Resealing';
    if (has(/\bwindow\b/i)) {
        const replacement = actionBefore('replace|renew|install new', 'windows?')
            || nounAction('windows?', 'replacement|renewal');
        if (replacement) return 'Window Replacement';
        const repair = actionBefore('make good|repair|reinstate', 'windows?')
            || nounAction('windows?', 'repair|repairs|reinstatement');
        if (repair) return 'Window Repair';
    }
    if (has(/\bdoor\b/i) && has(/\bsealant|\breseal|\bseal\b/i)) return 'Door Resealing';
    if (has(/\bdoor\b/i)) {
        const replacement = actionBefore('replace|renew|install new', 'doors?')
            || nounAction('doors?', 'replacement|renewal');
        if (replacement) return 'Door Replacement';
        const repair = actionBefore('make good|repair|reinstate', 'doors?')
            || nounAction('doors?', 'repair|repairs|reinstatement');
        if (repair) return 'Door Repair';
    }
    if (has(/\broof\b/i) && has(/\bwaterproof\w*|\bmembrane\b/i)) return 'Membrane Waterproofing';
    if (has(/\bgutter\b/i)) {
        if (gutterClearing) return 'Gutter Clearing';
        const gutterRepair = actionBefore('make good|repair|replace|renew|reinstate', 'gutters?|gutter run')
            || nounAction('gutters?|gutter run', 'repair|repairs|replacement|renewal|reinstatement');
        if (gutterRepair) return 'Gutter Repair';
    }
    if (has(/\beaves?\b/i)) return eaveRepair ? 'Eave Repair' : '';
    if (has(/\bdrain(?:age)?\b/i)
        && (actionBefore('clear|clean|install|make good|repair|replace|renew|reinstate', 'drains?|drainage')
            || nounAction('drains?|drainage', 'clearing|cleaning|installation|repair|repairs|replacement|renewal|reinstatement'))) {
        return 'Drainage Repair';
    }
    if (has(/\bsealant|\breseal|\bseal\b/i)) return 'Joint Resealing';
    if (has(/\bpaint\w*\b/i)) return 'Painting and Make-Good';
    return '';
}

// A title is derived from the work, never from the lines around it: NOTE and
// images lines are conditions, and the protection and cleanup lines are
// set-up (classifySharedSiteLogisticsInclusion, the same test the logistics
// de-dupe uses). On 2026-10-12 the derivation titled a shower job "Unit 3 -
// Temporary Protection Installation" from the protection line.
function namesItemWork(line) {
    const text = clean(line && typeof line === 'object'
        ? (line.action || line.text || line.description || line.title)
        : line);
    if (!text) return false;
    if (/^NOTE\b/i.test(text) || /^refer\s+to\s+the\s+images\b/i.test(text)) return false;
    return !classifySharedSiteLogisticsInclusion(text);
}

const WORK_LINE_FIELDS = ['inclusions', 'sequence_of_works', 'remediation_sequence'];

// A shallow copy holding only the work lines, used only to derive a title.
function withWorkLinesOnly(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
    const copy = { ...value };
    for (const field of WORK_LINE_FIELDS) {
        if (Array.isArray(copy[field])) copy[field] = copy[field].filter(namesItemWork);
    }
    const override = copy.client_scope_override;
    if (override && typeof override === 'object' && Array.isArray(override.inclusions)) {
        copy.client_scope_override = { ...override, inclusions: override.inclusions.filter(namesItemWork) };
    }
    return copy;
}

function physicalSentenceFallback(text) {
    const actionPattern = /\b(repair|replace|renew|rebuild|reinstate|install|remove|reconstruct|waterproof|repoint|seal|paint|clear)\b/i;
    const sentence = clean(text)
        .split(/[.;]/)
        .map(value => value.trim())
        .find(value => actionPattern.test(value));
    if (!sentence) return GENERATED_TITLE_REVIEW_MARKER;
    const actionMatch = sentence.match(actionPattern);
    const action = actionMatch?.[1]?.toLowerCase() || '';
    const work = actionMatch ? sentence.slice((actionMatch.index || 0) + actionMatch[0].length) : sentence;
    const words = work
        .split(/\b(?:at|along|across|above|below|beside|for|from|in|on|to|within)\b/i)[0]
        .split(/\s+/)
        .filter(Boolean)
        // An article is not part of a label ("The Floor Tiles and Bedding
        // Removal").
        .filter(word => !/^(?:a|an|the|all|affected|damaged|deteriorated|existing|failed|new)$/i.test(word))
        .slice(0, 5)
        .join(' ')
        .replace(/[,;:]$/, '');
    const workNoun = {
        repair: 'Repair',
        replace: 'Replacement',
        renew: 'Renewal',
        rebuild: 'Rebuilding',
        reinstate: 'Reinstatement',
        install: 'Installation',
        remove: 'Removal',
        reconstruct: 'Reconstruction',
        waterproof: 'Waterproofing',
        repoint: 'Repointing',
        seal: 'Sealing',
        paint: 'Painting',
        clear: 'Clearing',
    }[action] || 'Works';
    const title = titleCase(`${words} ${workNoun}`);
    return isInternalStrategyScopeTitle(title)
        ? GENERATED_TITLE_REVIEW_MARKER
        : title;
}

// The work-type title the rules derive from the item's and the sources' work
// lines ("<Location> - <Work type>"), with the evidence it read.
function derivedWorkTypeTitle(item = {}, sourceItems = []) {
    const workItem = withWorkLinesOnly(item);
    const workSources = (Array.isArray(sourceItems) ? sourceItems : []).map(withWorkLinesOnly);
    // One field or line per clause for the work-type rules: joined with a
    // space, a step with no full stop ran into the next field's reason ("...
    // waterproof silicone sealant Window reseal Water runs down the wall into
    // the Unit 4 shower recess") and made a window job shower work.
    const text = textValues([workItem, ...workSources]).join('\n');
    const workType = physicalWorkType(text);
    const location = physicalLocation(text);
    const title = workType && location && !workType.toLowerCase().includes(location.toLowerCase())
        ? `${location} - ${workType}`
        : workType;
    return { title, location, workItem, workSources, text };
}

/**
 * A title for the whole item, derived from its work lines by the work-type
 * rules, or '' when there is none: no rule names the work, the row's
 * subheadings name two or more areas (one work type names only one of them),
 * or the title needs repair (TITLE_REPAIR_CODES; a label that is only
 * over-long wraps on the quote and the title warning reports it, as for the
 * model's own labels). Never the sentence fallback, which names the first
 * step. The location comes from the same words, so a unit or lot they name is
 * in the title; the Stage 3 fold uses it when no source field passes and
 * applies the RFQ's location evidence on top.
 */
export function deriveWholeItemScopeTitle(item = {}, { sourceItems = [] } = {}) {
    if (subheadingAreas(item).size >= 2) return '';
    const { title } = derivedWorkTypeTitle(item, sourceItems);
    return title && !scopeTitleNeedsRepair(title) ? title : '';
}

/**
 * Resolve generated-only scope copy to a physical client title. It never
 * changes scope, quantities, inclusions or price. Explicit builder authorship
 * returns the original title and object untouched at the caller boundary.
 *
 * A source title replaces the row's title only when it keeps the place that
 * title names (keepsPlaceOf) and meets the no-location rule against the row's
 * own words; a derived title that drops that place gives the review marker
 * instead. The review marker (GENERATED_TITLE_REVIEW_MARKER) stays: the
 * Stage 3 fold leaves it after trying every source field and the work-type
 * derivation against the RFQ's location, which no later caller has, so its
 * readiness block stands until the builder writes the title. 2026-10-12: the
 * Stage 4 sanitise turned the fold's marker for a Unit 3 shower job into
 * "The Floor Tiles and Bedding Removal", with no unit and only the strip-out.
 */
export function resolveGeneratedClientScopeTitle(
    item = {},
    { sourceItems = [], authoredByUser = isExplicitUserAuthoredScopeItem(item) } = {},
) {
    const current = clean(item?.title);
    if (authoredByUser || (current && !scopeTitleNeedsRepair(current))) return current;
    if (current.toLowerCase() === GENERATED_TITLE_REVIEW_MARKER.toLowerCase()) return current;

    const evidence = itemLocationEvidence(item);
    const placed = title => keepsPlaceOf(title, current) && namesLocationFor(title, evidence);
    const sourceTitle = explicitSafeTitle(sourceItems, title => !scopeTitleNeedsRepair(title) && placed(title));
    if (sourceTitle) return sourceTitle;
    // Physical wording in the wrong shape (a standard number, a sentence) is
    // kept rather than derived again from the evidence (TITLE_REPAIR_CODES):
    // the item's own title first, then a source title, as before the check.
    if (current && !isInternalStrategyScopeTitle(current)) return current;
    const physicalSourceTitle = explicitSafeTitle(
        sourceItems,
        title => !isInternalStrategyScopeTitle(title) && placed(title),
    );
    if (physicalSourceTitle) return physicalSourceTitle;

    // A derived title keeps the place too. The rules know only some places
    // (no "laundry"), so when the derivation drops the one the title names,
    // the builder is asked: 2026-10-12, "Laundry - Remediation Works" became
    // "Floor Tiles and Bedding Removal", with no laundry and only the
    // strip-out.
    const keptPlaceOrReview = title => (keepsPlaceOf(title, current) ? title : GENERATED_TITLE_REVIEW_MARKER);
    const { title: workTitle, location, workItem, workSources, text } = derivedWorkTypeTitle(item, sourceItems);
    if (workTitle) return keptPlaceOrReview(workTitle);
    const physicalFallback = physicalSentenceFallback(
        physicalActionEvidenceText(workItem, workSources) || text,
    );
    if (physicalFallback === GENERATED_TITLE_REVIEW_MARKER) return physicalFallback;
    return keptPlaceOrReview(location ? `${location} - ${physicalFallback}` : physicalFallback);
}

export function sanitiseGeneratedClientScopeItem(item, options = {}) {
    if (!item || typeof item !== 'object') return item;
    const authoredByUser = options.authoredByUser ?? isExplicitUserAuthoredScopeItem(item);
    if (authoredByUser) return item;
    const title = resolveGeneratedClientScopeTitle(item, { ...options, authoredByUser: false });
    return title && title !== item.title ? { ...item, title } : item;
}

function lowerForObjective(value) {
    const text = clean(value);
    if (!text) return '';
    return text
        .replace(/[.!?]+$/, '')
        .toLowerCase()
        .replace(/\b(unit|lot|apartment)\b/g, word => word.charAt(0).toUpperCase() + word.slice(1));
}

function located(preposition, location) {
    if (!location) return '';
    const article = /^(?:Unit|Lot|Apartment)\b/.test(location) ? '' : 'the ';
    return ` ${preposition} ${article}${location}`;
}

// A bracket that states how the item is priced ("(Price Per Bay)",
// "(Rate Per m2)", "(Provisional Allowance)") belongs to the price, not to the
// work the objective names.
const PRICING_BASIS_BRACKET = /\s*\((?=[^)]*(?:\b(?:price|priced|pricing|rate|rates|allowance|lump\s*sum|provisional|pc\s*sum|prime\s*cost|each)\b|(?<!\bas\s)\bper\s+\w))[^)]*\)/gi;

function withoutPricingBasis(value) {
    return clean(String(value || '').replace(PRICING_BASIS_BRACKET, ' '));
}

// A work phrase whose last word names an activity reads after "carry out"
// ("carry out shower recess waterproofing", "carry out gutter clean"). One that
// names a thing ("storage cage", "balustrade") does not: it needs a verb.
const ACTIVITY_HEAD = /(?:ing|tion|sion|ment)s?$|^(?:works?|repairs?|removals?|renewals?|disposal|maintenance|clean|clear|test|check|upgrades?|good|make-good)$/i;
// A work phrase that already opens with a verb is the action itself, unless
// the verb is used as a noun (see readOpeningVerbs).
const OPENING_VERBS = [
    'apply', 'construct', 'demolish', 'erect', 'excavate', 'fabricate', 'install',
    'investigate', 'lay', 'rebuild', 'rectify', 'reinstate', 'relocate', 'remediate',
    'remove', 'renew', 'repaint', 'repair', 'replace', 'repoint', 'reseal',
    'resurface', 'restore', 're-waterproof', 'supply', 'waterproof', 'paint',
    'clean', 'clear', 'seal',
];
const OPENING_VERB = new RegExp(`^(?:${OPENING_VERBS.join('|')})\\b`, 'i');
const OPENING_VERB_WORDS = new Set(OPENING_VERBS);

// A listed verb followed by one of these is being used as a noun:
// "repair of the wall", "repair works to the balcony", "paint work".
const NOUN_READING_AFTER_VERB = new Set(['of', 'works', 'work']);

/**
 * How words open, read against a set of base-form verbs. The verbs may be
 * joined by "and"/"or", and each may be followed by "out".
 * - 'verb': a word that is not a noun reading follows ("repair the roof",
 *   "supply and install a strip drain").
 * - 'noun': a noun reading follows ("repair of the wall", "paint works",
 *   "supply and install works").
 * - 'bare': nothing follows ("repair").
 * - '': the words do not open with a listed verb, or a conjunction is not
 *   followed by one.
 * The objective connective (quote-objective.ts) and the derived objective
 * action both read a phrase with this one test.
 */
export function readOpeningVerbs(tokens, verbs) {
    let i = 0;
    while (i < tokens.length && verbs.has(tokens[i])) {
        i += 1;
        if (tokens[i] === 'out') i += 1;
        const next = tokens[i];
        if (next === undefined) return 'bare';
        if (NOUN_READING_AFTER_VERB.has(next)) return 'noun';
        if (next !== 'and' && next !== 'or') return 'verb';
        i += 1;
    }
    return '';
}

function phraseWords(value) {
    return String(value || '')
        .toLowerCase()
        .split(/\s+/)
        .map(word => word.replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, ''))
        .filter(Boolean);
}

// What a description can say is done to a thing.
const THING_VERBS = [
    [/\bsuppl(?:y|ying)\s+and\s+(?:install|fix|fit)\w*/gi, 'supply and install'],
    [/\b(?:install|fit|fix)(?:s|ed|ing|ation|ment)?\b/gi, 'install'],
    [/\breplac(?:e|es|ed|ing|ement)\b/gi, 'replace'],
    [/\brepair(?:s|ed|ing)?\b/gi, 'repair'],
    [/\brepaint(?:s|ed|ing)?\b/gi, 'repaint'],
    [/\bpaint(?:s|ed|ing)?\b/gi, 'paint'],
    [/\bclean(?:s|ed|ing)?\b/gi, 'clean'],
    [/\bremov(?:e|es|ed|ing|al)\b/gi, 'remove'],
];
// Where the words a verb acts on stop.
const CLAUSE_END = /[.;:,!?]|\bthen\b/i;

// The last word of the thing ("storage cage" -> cage), matching its plural.
function thingNounPattern(work) {
    const head = String(work || '').split(/\s+/).filter(Boolean).at(-1) || '';
    const stem = head.replace(/([^s])s$/i, '$1');
    if (stem.length < 3) return null;
    return new RegExp(`\\b${stem.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i');
}

// One job from the verbs, or '' when they describe more than one.
function oneJob(mentions) {
    const jobs = new Set(mentions.map(mention => mention.verb));
    if (jobs.has('supply and install')) jobs.delete('install');
    if (jobs.has('repaint')) jobs.delete('paint');
    // Taking the thing out and putting one in is replacing it.
    if (jobs.has('remove') && (jobs.has('install') || jobs.has('supply and install') || jobs.has('replace'))) {
        jobs.delete('remove');
        jobs.delete('install');
        jobs.delete('supply and install');
        jobs.add('replace');
    }
    return jobs.size === 1 ? [...jobs][0] : '';
}

// The job the description does to the thing the title names. A verb counts
// when the words it acts on (up to the next verb or the end of its clause)
// name the thing: "Clean out debris, then replace the cracked skylight dome"
// replaces the skylight; it does not clean it. Removing the thing and
// installing one is replacing it. When the description does two different
// jobs to it, or says nothing, there is no verb and the caller calls it
// works rather than state the wrong job on the quote.
function describedVerbForThing(item, work) {
    const description = textValues([
        item?.descriptionBrief,
        item?.description_brief,
        item?.clientDescription,
        item?.description,
        item?.client_scope_override?.description,
    ]).join(' ');
    const found = [];
    for (const [pattern, verb] of THING_VERBS) {
        for (const match of description.matchAll(pattern)) {
            found.push({ start: match.index ?? 0, end: (match.index ?? 0) + match[0].length, verb });
        }
    }
    found.sort((a, b) => (a.start - b.start) || ((b.end - b.start) - (a.end - a.start)));
    // A verb inside a longer one ("install" in "supply and installation")
    // is part of it.
    const mentions = [];
    let reach = -1;
    for (const mention of found) {
        if (mention.start < reach) continue;
        mentions.push(mention);
        reach = mention.end;
    }
    const noun = thingNounPattern(work);
    const onThing = noun
        ? mentions.filter((mention, index) => {
            const next = mentions[index + 1];
            const words = description.slice(mention.end, next ? next.start : description.length);
            return noun.test(words.split(CLAUSE_END)[0] || '');
        })
        : [];
    return oneJob(onThing.length > 0 ? onThing : mentions);
}

function objectiveActionForScopeItem(item) {
    const title = withoutPricingBasis(resolveGeneratedClientScopeTitle(item));
    if (isInternalStrategyScopeTitle(title)) return 'carry out the physical works described below';
    const [rawLocation, ...rawWork] = title.split(/\s+-\s+/);
    const hasLocation = rawWork.length > 0;
    const location = hasLocation ? lowerForObjective(rawLocation) : '';
    const work = lowerForObjective(hasLocation ? rawWork.join(' - ') : rawLocation);
    const evidence = evidenceText(item);
    let action;

    if (/^waterproofing and concrete substrate rectification$/i.test(work) && location) {
        action = `rectify the ${location} waterproofing and associated concrete substrate`;
    } else if (/^(?:membrane\s+)?waterproofing$/i.test(work)) {
        const isReplacementWaterproofing = /\bre[- ]?waterproof|\bwaterproofing remediation\b|\b(?:remove|replace)[\s\S]{0,80}\b(?:membrane|waterproofing)\b|\b(?:failed|existing)\s+(?:membrane|waterproofing)\b/i
            .test(evidence);
        const verb = isReplacementWaterproofing ? 're-waterproof' : 'waterproof';
        action = `${verb} ${location ? `the ${location}` : 'the nominated work area'}`;
    } else if (/^trench drain rebate repairs?$/i.test(work)) {
        action = `carry out trench drain rebate repairs${located('to', location)}`;
    } else if (/\brepairs?$/i.test(work)) {
        const physicalWorks = work.replace(/\brepair$/i, 'repairs');
        action = `carry out ${physicalWorks}${located('to', location)}`;
    } else if (/\brectification$/i.test(work)) {
        action = `rectify ${work.replace(/\s+rectification$/i, '')}${located('at', location)}`;
    } else if (/\breplacement$/i.test(work)) {
        const element = work.replace(/\s+replacement$/i, '');
        const object = /^(?:a|an|the|all|Unit|Lot|Apartment)\b/i.test(element) ? element : `the ${element}`;
        action = `replace ${object}${located('at', location)}`;
    } else if (/\binstallation$/i.test(work)) {
        action = `install ${work.replace(/\s+installation$/i, '')}${located('at', location)}`;
    } else if (OPENING_VERB.test(work)) {
        // "Paint Works" and "Repair of Balustrade" use the verb as a noun:
        // they name an activity, carried out. "Repair Cracked Ceiling" is the
        // action itself.
        action = readOpeningVerbs(phraseWords(work), OPENING_VERB_WORDS) === 'noun'
            ? `carry out ${work}${located('at', location)}`
            : `${work}${located('at', location)}`;
    } else if (ACTIVITY_HEAD.test(work.split(/\s+/).at(-1) || '')) {
        action = `carry out ${work}${located('at', location)}`;
    } else {
        // The title names a thing. Use what the description says is done to
        // it; with nothing said, call it works ("gutter works") rather than
        // guess a verb.
        const verb = describedVerbForThing(item, work);
        const object = /^(?:a|an|the|all|Unit|Lot|Apartment)\b/i.test(work) ? work : `the ${work}`;
        action = verb
            ? `${verb} ${object}${located('at', location)}`
            : `carry out ${work.replace(/([^s])s$/i, '$1')} works${located('at', location)}`;
    }

    if (/\bengineer(?:ing|ed|s)?\b/i.test(evidence)
        && /\b(?:design|drawing|specification)s?\b/i.test(evidence)
        && !/\breferenced engineering design\b/i.test(action)) {
        action += ' in accordance with the referenced engineering design';
    }
    return action;
}

function naturalJoin(values) {
    if (values.length <= 1) return values[0] || '';
    if (values.length === 2) return `${values[0]} and ${values[1]}`;
    return `${values.slice(0, -1).join(', ')}, and ${values.at(-1)}`;
}

function isNonPhysicalAdministrationItem(item = {}) {
    if (item?.isAdminItem === true || item?.adminItem === true || item?.systemManaged === true) return true;
    const kind = clean(item?.adminKind || item?.statutoryKind || item?.pricingKind).toLowerCase();
    if (/\b(?:hbcf|insurance|statutory|administration|admin)\b/.test(kind)) return true;
    return /^(?:hbcf|home building compensation fund|statutory administration|administration fee)\b/i
        .test(clean(item?.title));
}

export function derivePlainPhysicalScopeAction(scope = []) {
    const actions = (Array.isArray(scope) ? scope : [])
        .filter(item => item && typeof item === 'object')
        .filter(item => !isNonPhysicalAdministrationItem(item))
        .map(objectiveActionForScopeItem)
        .filter(Boolean);
    if (actions.length > 1 && actions.every(action => /^carry out\s+/i.test(action))) {
        const works = actions.map(action => action.replace(/^carry out\s+/i, ''));
        // "roof works" and "gutter works" read as "roof and gutter works".
        if (works.every(work => /\s+works$/i.test(work))) {
            return `carry out ${naturalJoin(works.map(work => work.replace(/\s+works$/i, '')))} works`;
        }
        return `carry out ${naturalJoin(works)}`;
    }
    return actions.length > 0
        ? naturalJoin(actions)
        : 'carry out the physical works described below';
}

export function generatedObjectiveContainsInternalStrategyJargon(value) {
    const objective = clean(value);
    if (hasInternalStrategyJargon(objective)
        || hasTechnicalRegisterTitleDetail(objective, { allowReferencedDesign: true })) return true;
    const withoutFurniture = objective
        .replace(/^the\s+objective\s+of\s+this\s+(?:revised\s+)?quote\s+is\s+to\s+provide\s+(?:a\s+price|prices|pricing)\s+(?:to|for)\s+/i, '')
        .replace(/^(?:carry out|undertake|complete|perform)\s+/i, '')
        .replace(/\s+at\s+the\s+above\s+property\.?$/i, '');
    return GENERIC_SCOPE_TITLE.test(withoutFurniture)
        || GENERIC_OBJECTIVE_ACTION.test(withoutFurniture);
}
